import { favoritePushWindowOpen, LISTING_ACTION_MAX_LENGTH } from '@pulsify/shared'
import type { CreateListingActionRequest, ListingActionDto } from '@pulsify/shared/dto'
import { Loader2, Megaphone } from 'lucide-react'
import { useId, useState } from 'react'
import * as m from '~/paraglide/messages'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '~/shared/ui/alert-dialog'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { SegmentedControl } from '~/shared/ui/segmented-control'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/shared/ui/select'
import { Textarea } from '~/shared/ui/textarea'
import {
  type ActionComposerProblem,
  actionComposerProblem,
  actionReachesFavorites,
  toCreateActionRequest,
} from '../lib/action-composer'
import {
  type ActionEndChoice,
  actionEndDays,
  DEFAULT_ACTION_END,
  isActionEndChoiceValid,
} from '../lib/action-end'
import { actionDayLabel } from '../lib/action-labels'

const PROBLEM_MESSAGES: Record<ActionComposerProblem, () => string> = {
  empty: m.actionProblemEmpty,
  too_long: () => m.actionProblemTooLong({ max: LISTING_ACTION_MAX_LENGTH }),
  end_passed: m.actionProblemEndPassed,
}

interface ActionComposerCardProps {
  placeName: string
  favoriteCount: number
  /** The running action a new one would end. */
  replaces: ListingActionDto | null
  locale: 'de' | 'en'
  sending: boolean
  onSend: (input: CreateListingActionRequest) => Promise<boolean>
}

export function ActionComposerCard({
  placeName,
  favoriteCount,
  replaces,
  locale,
  sending,
  onSend,
}: ActionComposerCardProps) {
  const textId = useId()
  const [text, setText] = useState('')
  const [choice, setChoice] = useState<ActionEndChoice>(DEFAULT_ACTION_END)
  const now = new Date()
  const days = actionEndDays(now)
  const problem = actionComposerProblem(text, choice, now)
  const reaches = !isActionEndChoiceValid(choice, now) || actionReachesFavorites(choice, now)
  const reach =
    favoriteCount === 0
      ? m.actionNoFavorites()
      : favoriteCount === 1
        ? m.actionReachOne()
        : m.actionReachMany({ count: favoriteCount })
  const confirmation = reaches
    ? favoriteCount === 0
      ? m.actionConfirmNone()
      : favoriteCount === 1
        ? m.actionConfirmOne()
        : m.actionConfirmMany({ count: favoriteCount })
    : m.actionConfirmNoPush()

  async function send() {
    if (problem) return
    if (await onSend(toCreateActionRequest(text, choice, new Date()))) setText('')
  }

  return (
    <Card className="flex flex-col gap-5 p-6">
      <h2 className="font-semibold font-serif text-2xl text-foreground tracking-tight">
        {m.actionComposerHeading()}
      </h2>
      <div className="flex flex-col gap-2">
        <Label htmlFor={textId}>{m.actionTextLabel()}</Label>
        <Textarea
          id={textId}
          value={text}
          rows={3}
          maxLength={LISTING_ACTION_MAX_LENGTH}
          placeholder={m.actionTextPlaceholder()}
          onChange={(event) => setText(event.target.value)}
        />
        <p className="text-right text-muted-foreground text-xs">
          {m.actionTextCount({ count: text.trim().length, max: LISTING_ACTION_MAX_LENGTH })}
        </p>
      </div>
      <div className="flex flex-col gap-3">
        <SegmentedControl
          label={m.actionEndLabel()}
          value={choice.mode}
          options={[
            { value: 'today', label: m.actionEndToday() },
            { value: 'day', label: m.actionEndDay() },
          ]}
          onChange={(mode) =>
            setChoice(mode === 'today' ? DEFAULT_ACTION_END : { mode: 'day', date: days[0] ?? '' })
          }
        />
        {choice.mode === 'today' ? (
          <Input
            type="time"
            className="w-40"
            aria-label={m.actionEndTimeLabel()}
            value={choice.time}
            onChange={(event) => setChoice({ mode: 'today', time: event.target.value })}
          />
        ) : (
          <Select value={choice.date} onValueChange={(date) => setChoice({ mode: 'day', date })}>
            <SelectTrigger className="w-64" aria-label={m.actionEndDayLabel()}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {days.map((date) => (
                <SelectItem key={date} value={date}>
                  {actionDayLabel(date, locale)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      <div className="rounded-xl border bg-muted/40 p-4">
        <p className="mb-2 font-medium text-muted-foreground text-xs uppercase tracking-wide">
          {m.actionPreviewHeading()}
        </p>
        <div className="flex items-start gap-3 rounded-lg bg-card p-3 shadow-sm">
          <Megaphone className="mt-0.5 size-4 shrink-0 text-brand-subtle-foreground" aria-hidden />
          <div className="min-w-0">
            <p className="font-semibold text-foreground text-sm">{placeName}</p>
            <p className="break-words text-foreground text-sm">
              {text.trim() || m.actionTextPlaceholder()}
            </p>
          </div>
        </div>
        {reaches ? (
          <>
            <p className="mt-3 text-muted-foreground text-sm">{reach}</p>
            {!favoritePushWindowOpen(now) && (
              <p className="mt-1 text-muted-foreground text-sm">{m.actionNightNote()}</p>
            )}
          </>
        ) : (
          <p className="mt-3 text-muted-foreground text-sm">{m.actionNoPushBeforeMorning()}</p>
        )}
      </div>
      {problem && text.length > 0 && (
        <p className="text-destructive text-sm">{PROBLEM_MESSAGES[problem]()}</p>
      )}
      {replaces && <p className="text-muted-foreground text-sm">{m.actionReplacesRunning()}</p>}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button type="button" className="w-fit" disabled={problem !== null || sending}>
            {sending && <Loader2 className="animate-spin" />}
            {m.actionSend()}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{m.actionConfirmTitle()}</AlertDialogTitle>
            <AlertDialogDescription>{confirmation}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{m.cancel()}</AlertDialogCancel>
            <AlertDialogAction onClick={send}>{m.actionSend()}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
